import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { BlockedNumber } from '../types';

interface BlockedNumbersTableProps {
  blockedNumbers: BlockedNumber[];
  onDelete: (id: string) => void;
  deletingId: string | null;
}

export const BlockedNumbersTable: React.FC<BlockedNumbersTableProps> = ({
  blockedNumbers,
  onDelete,
  deletingId,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('blockedNumbers.phoneNumber')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('blockedNumbers.reason')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('blockedNumbers.blockedBy')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('blockedNumbers.blockedAt')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {blockedNumbers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          block
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('blockedNumbers.noBlockedNumbers')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('blockedNumbers.noBlockedNumbersDescription')}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                blockedNumbers.map(entry => (
                  <tr
                    key={entry.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="material-icons text-base text-red-500">block</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {entry.number}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {entry.reason || (
                        <span className="italic text-gray-400 dark:text-gray-500">
                          {t('blockedNumbers.noReason')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {entry.blocked_by_name ? (
                        <Badge variant="secondary" size="sm">
                          {entry.blocked_by_name}
                        </Badge>
                      ) : (
                        <span className="italic text-gray-400 dark:text-gray-500 text-sm">
                          {t('blockedNumbers.system')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">
                      {new Date(entry.created).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(entry.id)}
                        disabled={deletingId === entry.id}
                        className="p-1 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title={t('blockedNumbers.unblock')}
                      >
                        <span className="material-icons text-lg">
                          {deletingId === entry.id ? 'hourglass_empty' : 'delete'}
                        </span>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
